import { ApiError, toParam, type ApiFailure } from './types'
import { maintenanceDetected } from './maintenance'
import { entryRefusedDetected } from './entry'
import { ACT_AS_HEADER, actingAs } from './act-as'
import { visibilityOf, type Answer } from './answer'
import type {
  AccessPeople, AccessPeopleFilter, AccessPersonPage,
  AccessApprovalCard, AccessApprovalFilter, AccessApprovals, AccessDecision,
  AccessHrRoleBody, AccessHrRoleEffect, AccessHrRolePage, AccessHrRoles, AccessHierarchyPreview,
  AccessProblems, AccessProblemItems, AccessSync, AccessHeldPull, AccessAcceptResult, AccessEmailDomains, AccessEmailDomainEffect,
  AccessStaff, AccessHistory, AccessOrgCodes, AccessPersonSettingsBody, AccessExtraScopeBody, AccessPersonEffect, AccessSignInLinkEffect,
  MpOverview, MpStockRow, MpPurchaseOrderRow, MpSalesMonth, MpItemStatusRow, MpOrderStatus,
  MpFilterRow, MpBreakdownRow, MpBreakdownKind, MpShipmentRow, MpSalesPerformanceRow,
  MpItemSummary, MpStockSort, MpStockGroupBy, MpShipmentView, MpItemOrderRow,
  InvFilterRow, InvOverview, InvMatrixRow, InvLotRow, InvGroupBy, InvWindow, InvAgeBand,
  StatementCompany, StatementCustomer, StatementKind, StatementResponse,
  ActAsCandidate,
  ActAsTarget,
  NewsItem,
  PublishNewsRequest,
  UpdateNewsRequest,
  MaintenanceStatus,
  MatrixNode,
  MatrixPath,
  RecvFilters,
  RecvFilterRow,
  DecomposeLevel,
  DecomposePath,
  Currency,
  DecomposeResponse,
  DsoNode,
  Me,
  WeekComparison,
  WeekDim,
  WeekTrend,
  SalespersonReview,
  WeekMetric,
  PermissionAnomaly,
  PermissionGrant,
  PermissionUser,
  ReportAccessEntry,
  GrantAccessRequest,
  GrantAccessResult,
  RoleHolder,
  SetRoleRequest,
  AdminReport,
  ColumnGovernance,
  SetColumnPermissionsRequest,
  ColumnPermissionChange,
  ColumnDefinition,
  ColumnSettingBody,
  ColumnSettingEffect,
  ColumnGroupPage,
  ColumnGroupBody,
  ColumnGroupEffect,
  SetMaintenanceRequest,
  TransactionPage,
  SalesBoardRow,
  SalesDimension,
  SalesDrillLevel,
  SalesExplorerDimension,
  SalesExplorerResponse,
  SalesFilterRow,
  SalesFilters,
  CurrencyRate,
  DevFlag,
  SetDevFlagRequest,
  MeasureDoc,
  MeasureRef,
  SalesLineFilterRow,
  SalesLineFilters,
  SalesLineSort,
  SalesLinesResponse,
  SalesOverview,
  SalesPeriod,
  SalesRange,
  SalesTreeResponse,
  SalesTrendRow,
  StoredGridLayout,
  StoredReaderView,
} from './types'

export type TokenProvider = () => Promise<string | null>

/**
 * The delimiter for a drill-path list: U+001F, the ASCII unit separator.
 *
 * NOT a comma. The company list splits on one safely because a DATAAREAID is
 * "r002"; a drill path carries customer, product and supplier NAMES, and
 * "Acme, Inc." split on a comma becomes two members that match nothing — a
 * filter that silently draws an empty chart. A control character cannot occur
 * in ERP text, so there is nothing to escape, and URLSearchParams encodes it.
 */
const PATH_SEP = '\u001F'

/** Query-string name per dimension, so the client and the endpoint agree once. */
const PATH_KEYS: [SalesDimension, string][] = [
  ['BusinessUnit', 'pathBu'],
  ['BusinessTeam', 'pathBt'],
  ['Salesperson', 'pathSp'],
  ['Customer', 'pathCust'],
  ['Product', 'pathProd'],
  ['Supplier', 'pathSup'],
]

/**
 * SORTED and de-duplicated, which is not tidiness.
 *
 * The server caches on the parameter value verbatim, so the same three teams
 * ticked in a different order would be two cache entries for one question: the
 * reader waits twice and the platform stores the same rows twice. The API sorts
 * again — this is the cheap half of the same guarantee, and it also keeps the
 * URL stable, which matters because the URL is what a reader copies to somebody
 * else.
 */
function pathList(members: string[]): string {
  return [...new Set(members)].sort().join(PATH_SEP)
}

/**
 * The Receivables standing filter bar, written onto a query string.
 *
 * ONE FUNCTION FOR FOUR ENDPOINTS, mirroring the single `RecvFilters` class the
 * API binds. The matrix, the transactions grid, DSO and Week Comparison sit
 * behind one bar, so a parameter spelled differently on one of them would make
 * two screens answer different questions with nothing on either saying so.
 *
 * Endpoints that cannot honour a value simply ignore it — the DSO procedure
 * takes four of these and the weekly one five, because their sources carry no
 * more. The page already hides the controls those tabs cannot use; sending the
 * whole bar regardless keeps THIS function free of a second copy of that rule.
 *
 * The company list joins on a COMMA, matching the server's split, and is sorted
 * for the same reason `pathList` sorts: the response cache keys on the value
 * verbatim, so the same three companies ticked in a different order would be
 * two entries for one question.
 */
function recvFilterParams(q: URLSearchParams, f?: RecvFilters): void {
  if (!f) return
  if (f.company?.length) q.set('fCompany', [...new Set(f.company)].sort().join(','))
  if (f.bu) q.set('fBu', f.bu)
  if (f.bt) q.set('fBt', f.bt)
  if (f.sp) q.set('fSp', f.sp)
  if (f.cust) q.set('fCust', f.cust)
  if (f.aging) q.set('fAging', f.aging)
  if (f.trxType) q.set('fType', f.trxType)
  if (f.terms) q.set('fTerms', f.terms)
  if (f.region) q.set('fRegion', f.region)
  if (f.doc) q.set('fDoc', f.doc)
  if (f.overdueOnly) q.set('fOverdue', 'true')
  // Zero is a legitimate "due today or later" and must survive the truthiness
  // test that drops every other unset value.
  if (f.minDaysOverdue !== undefined && f.minDaysOverdue !== null) {
    q.set('fMinOverdue', String(f.minDaysOverdue))
  }
}

/**
 * The only place the browser talks to the API.
 *
 * Two rules it enforces on every call:
 *
 *   1. NOTHING about identity or scope is ever sent. No user id, no company,
 *      no filter. The server derives all of it from the validated token and the
 *      database. If a request body could carry a user id, the front end would
 *      become part of the security boundary — and a browser is not a place to
 *      put one.
 *
 *   2. Failures are decoded into distinct kinds, not collapsed into one error.
 *      A 403 for "no permissions" and a 503 for maintenance require different
 *      screens and different advice, and the difference is lost forever if it
 *      is flattened here.
 */
export class ApiClient {
  readonly #getToken: TokenProvider

  constructor(getToken: TokenProvider) {
    this.#getToken = getToken
  }

  /**
   * Who this session is, which under Login As is the person being acted as —
   * plus `actor`, naming whoever is really at the keyboard.
   *
   * Takes no identity parameter, and neither does anything else on this client.
   * That is rule 1 above, finally true without an exception: acting is one
   * header set once in `#send`, not an argument thirty methods had to remember.
   */
  me(): Promise<Me> {
    return this.#send('GET', '/api/v1/me')
  }

  /**
   * Everyone this caller could act as, and everyone they could not.
   *
   * Both populations in one list — see {@link ActAsCandidate}. Refused with 409
   * while already acting, because the list would otherwise be computed for the
   * borrowed identity.
   */
  actAsCandidates(search?: string): Promise<ActAsCandidate[]> {
    const q = search ? `?search=${encodeURIComponent(search)}` : ''
    return this.#send('GET', `/api/v1/act-as/candidates${q}`)
  }

  /**
   * Starts acting as somebody. Writes the ACT_AS_BEGIN audit row and returns the
   * target.
   *
   * Called ONCE, at the switch. Everything after it rides the X-Act-As header
   * that `#send` attaches, so there is no per-request cost and no second place
   * that can disagree about whose session this is.
   */
  beginActAs(objectId: string): Promise<ActAsTarget> {
    return this.#send('POST', '/api/v1/act-as/begin', { objectId })
  }

  /**
   * One level of the receivables matrix — the children of the node identified by
   * `path`, or the top level when `path` is empty.
   *
   * One request per expansion, which is what Power BI does with the same visual.
   * Fetching the whole tree instead would mean fetching the fact table down to
   * document level before the first row could be painted.
   */
  receivablesMatrix(
    path: MatrixPath = {},
    options: { includeClosed?: boolean; currency?: Currency; filters?: RecvFilters } = {},
  ): Promise<Answer<MatrixNode[]>> {
    const q = new URLSearchParams()
    if (path.bu) q.set('bu', path.bu)
    if (path.bt) q.set('bt', path.bt)
    if (path.sp) q.set('sp', path.sp)
    if (path.customer) q.set('customer', path.customer)
    if (options.includeClosed) q.set('includeClosed', 'true')
    { const ccy = toParam(options.currency); if (ccy) q.set('currency', ccy) }
    recvFilterParams(q, options.filters)

    const qs = q.toString()
    return this.#read(`/api/v1/reports/recv-aging/matrix${qs ? `?${qs}` : ''}`)
  }

  /**
   * Every value the standing filter bar may offer this caller, already scoped.
   *
   * ONE CALL FOR EIGHT MENUS, and the response is not a lookup table: the
   * procedure filters it by the caller's own scope and withholds the
   * salesperson kind entirely from anyone without SLS_Mod, so a menu this
   * client never receives is a control the page never draws. Nothing here
   * re-sorts or re-groups; the server decided both.
   */
  receivablesFilterOptions(): Promise<RecvFilterRow[]> {
    return this.#send('GET', '/api/v1/reports/recv-aging/filters')
  }

  /**
   * One page of open documents — the Aging By Transaction table.
   *
   * Paged on the SERVER, and the page size is clamped there too (500). This
   * endpoint is the one place the platform can be asked for raw documents
   * rather than totals, so each page is a separate audited read: pulling the
   * whole book leaves a hundred rows in the access log rather than one.
   */
  receivablesTransactions(
    options: {
      bu?: string
      bt?: string
      customer?: string
      page?: number
      pageSize?: number
      sortBy?: string
      sortDesc?: boolean
      includeClosed?: boolean
      currency?: Currency
      filters?: RecvFilters
    } = {},
  ): Promise<Answer<TransactionPage>> {
    const q = new URLSearchParams()
    if (options.bu) q.set('bu', options.bu)
    if (options.bt) q.set('bt', options.bt)
    if (options.customer) q.set('customer', options.customer)
    if (options.page && options.page > 1) q.set('page', String(options.page))
    if (options.pageSize) q.set('pageSize', String(options.pageSize))
    if (options.sortBy) q.set('sortBy', options.sortBy)
    if (options.sortDesc === false) q.set('sortDesc', 'false')
    if (options.includeClosed) q.set('includeClosed', 'true')
    { const ccy = toParam(options.currency); if (ccy) q.set('currency', ccy) }
    recvFilterParams(q, options.filters)

    const qs = q.toString()
    return this.#read(`/api/v1/reports/recv-aging/transactions${qs ? `?${qs}` : ''}`)
  }

  /**
   * One level of the decomposition tree.
   *
   * `groupBy` is the ONE report parameter in the platform that selects a COLUMN
   * rather than a value. It is validated against a fixed list inside the
   * procedure and never interpolated, and the response tells the client which
   * breakdowns it may offer next — a caller who cannot see a column is not
   * offered it.
   */
  receivablesDecompose(
    options: DecomposePath & {
      groupBy?: DecomposeLevel
      topN?: number
      includeClosed?: boolean
      currency?: Currency
    } = {},
  ): Promise<Answer<DecomposeResponse>> {
    const q = new URLSearchParams()
    if (options.groupBy) q.set('groupBy', options.groupBy)
    if (options.bu) q.set('bu', options.bu)
    if (options.bt) q.set('bt', options.bt)
    if (options.sp) q.set('sp', options.sp)
    if (options.customer) q.set('customer', options.customer)
    if (options.document) q.set('document', options.document)
    if (options.topN) q.set('topN', String(options.topN))
    if (options.includeClosed) q.set('includeClosed', 'true')
    { const ccy = toParam(options.currency); if (ccy) q.set('currency', ccy) }

    const qs = q.toString()
    return this.#read(`/api/v1/reports/recv-aging/decompose${qs ? `?${qs}` : ''}`)
  }

  /** One level of the Salesperson DSO hierarchy. */
  receivablesDso(
    options: { company?: string; bu?: string; bt?: string; filters?: RecvFilters } = {},
  ): Promise<Answer<DsoNode[]>> {
    const q = new URLSearchParams()
    if (options.company) q.set('company', options.company)
    if (options.bu) q.set('bu', options.bu)
    if (options.bt) q.set('bt', options.bt)
    recvFilterParams(q, options.filters)
    const qs = q.toString()
    return this.#read(`/api/v1/reports/recv-aging/dso${qs ? `?${qs}` : ''}`)
  }

  /**
   * Two weeks of one metric, side by side.
   *
   * `weekA` and `weekB` are any DAY inside the week you want; the server snaps
   * to the containing Sunday-to-Saturday week. Sending a day rather than a
   * range means the two ends of a comparison cannot be defined differently
   * from one another.
   */
  receivablesWeeks(
    options: {
      metric?: WeekMetric
      dim?: WeekDim
      weekA?: string
      weekB?: string
      currency?: Currency
      filters?: RecvFilters
    } = {},
  ): Promise<Answer<WeekComparison>> {
    const q = new URLSearchParams()
    if (options.metric) q.set('metric', options.metric)
    if (options.dim) q.set('dim', options.dim)
    if (options.weekA) q.set('weekA', options.weekA)
    if (options.weekB) q.set('weekB', options.weekB)
    { const ccy = toParam(options.currency); if (ccy) q.set('currency', ccy) }
    recvFilterParams(q, options.filters)
    const qs = q.toString()
    return this.#read(`/api/v1/reports/recv-aging/weeks${qs ? `?${qs}` : ''}`)
  }

  /**
   * The same metric over N consecutive weeks, oldest first.
   *
   * One request, not N: the server loops. The procedure still runs once per
   * week, so the audit still records each week separately — what is saved is
   * N token validations and N connection acquisitions, not N audited reads.
   */
  receivablesTrend(
    options: {
      metric?: WeekMetric
      dim?: WeekDim
      weeks?: number
      endWeek?: string
      currency?: Currency
    } = {},
  ): Promise<Answer<WeekTrend>> {
    const q = new URLSearchParams()
    if (options.metric) q.set('metric', options.metric)
    if (options.dim) q.set('dim', options.dim)
    if (options.weeks) q.set('weeks', String(options.weeks))
    if (options.endWeek) q.set('endWeek', options.endWeek)
    { const ccy = toParam(options.currency); if (ccy) q.set('currency', ccy) }
    const qs = q.toString()
    return this.#read(`/api/v1/reports/recv-aging/trend${qs ? `?${qs}` : ''}`)
  }

  /**
   * The printable per-salesperson review: four metrics, two weeks, broken down
   * by customer.
   *
   * The name is sent as typed. The database blanks it for a caller who may not
   * see salesperson names, so an unprivileged caller gets identical figures
   * whatever they send — there is no roster to probe from here.
   */
  salespersonReview(options: {
    salesperson: string
    weekA?: string
    weekB?: string
    currency?: Currency
  }): Promise<Answer<SalespersonReview>> {
    const q = new URLSearchParams({ salesperson: options.salesperson })
    if (options.weekA) q.set('weekA', options.weekA)
    if (options.weekB) q.set('weekB', options.weekB)
    { const ccy = toParam(options.currency); if (ccy) q.set('currency', ccy) }
    return this.#read(`/api/v1/reports/recv-aging/salesperson-review?${q.toString()}`)
  }

  /* ------------------------------------------------------------------
     SALES — the merged Power BI sales report. Same rules as every other
     report call: nothing about identity is ever sent, viewAs only ever
     narrows, and a masked figure arrives null rather than zero.
     ------------------------------------------------------------------ */

  /** The KPI head for the chosen period, with the freshness watermark. */
  salesOverview(
    options: SalesFilters &
      SalesRange & { period?: SalesPeriod; currency?: Currency } = {},
  ): Promise<Answer<SalesOverview>> {
    const qs = salesQuery(options).toString()
    return this.#read(`/api/v1/reports/sales/overview${qs ? `?${qs}` : ''}`)
  }

  /**
   * One level of the explorer grid. `l1`/`l2` are the values already drilled
   * into; the chain per dimension is fixed in the procedure, so the client
   * never says which COLUMN to group by — only which member it opened.
   */
  salesExplorer(
    options: SalesFilters & SalesRange & {
      period?: SalesPeriod
      dimension?: SalesExplorerDimension
      /** The reader's own levels 2 to 4; omitted means the grouping's default chain. */
      drill2?: SalesDrillLevel
      drill3?: SalesDrillLevel
      drill4?: SalesDrillLevel
      l1?: string
      l2?: string
      l3?: string
      search?: string
      currency?: Currency
    } = {},
  ): Promise<Answer<SalesExplorerResponse>> {
    const q = salesQuery(options)
    if (options.dimension) q.set('dimension', options.dimension)
    if (options.drill2) q.set('drill2', options.drill2)
    if (options.drill3) q.set('drill3', options.drill3)
    if (options.drill4) q.set('drill4', options.drill4)
    if (options.l1) q.set('l1', options.l1)
    if (options.l2) q.set('l2', options.l2)
    if (options.l3) q.set('l3', options.l3)
    if (options.search) q.set('search', options.search)
    const qs = q.toString()
    return this.#read(`/api/v1/reports/sales/explorer${qs ? `?${qs}` : ''}`)
  }

  /** The six top-five boards for the chosen period. GP boards are absent — not empty — when masked. */
  salesLeaderboard(
    options: SalesFilters &
      SalesRange & { period?: SalesPeriod; currency?: Currency } = {},
  ): Promise<Answer<SalesBoardRow[]>> {
    const qs = salesQuery(options).toString()
    return this.#read(`/api/v1/reports/sales/leaderboard${qs ? `?${qs}` : ''}`)
  }

  /** Monthly sales and GP for this year and last, from real monthly amounts. */
  salesTrend(
    options: SalesFilters & { currency?: Currency } = {},
  ): Promise<Answer<SalesTrendRow[]>> {
    const qs = salesQuery(options).toString()
    return this.#read(`/api/v1/reports/sales/trend${qs ? `?${qs}` : ''}`)
  }

  /** One level of the decomposition tree, by sales or by GP. */
  salesTree(
    options: SalesFilters & SalesRange & {
      period?: SalesPeriod
      measure?: 'sales' | 'gp'
      groupBy?: SalesDimension
      customer?: string
      product?: string
      supplier?: string
      /**
       * The drill path: which members of each level are open. A list, because a
       * reader may open several members of a level at once.
       *
       * Separate from the standing filter bar's `bu`/`bt`/`sp` even where they
       * name the same column — both narrow, and until 23 Sep 2026 they shared a
       * parameter, so a tree step silently REPLACED the filter bar's team
       * instead of narrowing within it.
       */
      path?: Partial<Record<SalesDimension, string[]>>
      topN?: number
      currency?: Currency
    } = {},
  ): Promise<Answer<SalesTreeResponse>> {
    const q = salesQuery(options)
    if (options.measure) q.set('measure', options.measure)
    if (options.groupBy) q.set('groupBy', options.groupBy)
    if (options.customer) q.set('customer', options.customer)
    if (options.product) q.set('product', options.product)
    if (options.supplier) q.set('supplier', options.supplier)
    if (options.path) {
      for (const [dim, key] of PATH_KEYS) {
        const members = options.path[dim]
        if (members?.length) q.set(key, pathList(members))
      }
    }
    if (options.topN) q.set('topN', String(options.topN))
    const qs = q.toString()
    return this.#read(`/api/v1/reports/sales/tree${qs ? `?${qs}` : ''}`)
  }

  /**
   * One page of the posted sales ledger. Paged on the SERVER: a sort orders
   * every line the caller can see, not the hundred in front of them, and each
   * page is one audited read.
   */
  salesLines(
    options: SalesFilters &
      SalesRange &
      SalesLineFilters & {
        period?: SalesPeriod
        page?: number
        pageSize?: number
        sortBy?: SalesLineSort
        sortDesc?: boolean
        currency?: Currency
      } = {},
  ): Promise<Answer<SalesLinesResponse>> {
    const q = salesQuery(options)
    if (options.docType) q.set('docType', options.docType)
    if (options.district) q.set('district', options.district)
    if (options.location) q.set('location', options.location)
    if (options.kluber) q.set('kluber', options.kluber)
    if (options.search) q.set('search', options.search)
    // Sent only when turned OFF: the server's default is management's filter.
    if (options.hideZeroQty === false) q.set('hideZeroQty', 'false')
    if (options.page && options.page > 1) q.set('page', String(options.page))
    if (options.pageSize) q.set('pageSize', String(options.pageSize))
    if (options.sortBy) q.set('sortBy', options.sortBy)
    if (options.sortDesc === false) q.set('sortDesc', 'false')
    const qs = q.toString()
    return this.#read(`/api/v1/reports/sales/lines${qs ? `?${qs}` : ''}`)
  }

  /** The Invoice Lines page's own option lists, scoped like the standing bar's. */
  salesLineFilterOptions(): Promise<SalesLineFilterRow[]> {
    const q = new URLSearchParams()
    const qs = q.toString()
    return this.#send('GET', `/api/v1/reports/sales/line-filters${qs ? `?${qs}` : ''}`)
  }

  /**
   * The currencies a reader may restate figures into.
   *
   * Reference data, not a report: it belongs to none of the three, so it is
   * not under any report's maintenance window. An EMPTY LIST is a valid
   * answer — it means no currency currently has a rate the platform will
   * stand behind, and the Custom option simply does not appear.
   */
  currencyRates(): Promise<CurrencyRate[]> {
    return this.#send('GET', '/api/v1/reference/currencies')
  }

  /** The standing filter bar's options — computed inside the caller's scope. */
  salesFilterOptions(): Promise<SalesFilterRow[]> {
    const q = new URLSearchParams()
    const qs = q.toString()
    return this.#send('GET', `/api/v1/reports/sales/filters${qs ? `?${qs}` : ''}`)
  }

  permissionUsers(search?: string): Promise<PermissionUser[]> {
    const q = search ? `?search=${encodeURIComponent(search)}` : ""
    return this.#send("GET", `/api/v1/admin/permissions/users${q}`)
  }

  permissionUserDetail(email: string): Promise<PermissionGrant[]> {
    return this.#send("GET", `/api/v1/admin/permissions/users/${encodeURIComponent(email)}`)
  }

  permissionAnomalies(): Promise<PermissionAnomaly[]> {
    return this.#send("GET", "/api/v1/admin/permissions/anomalies")
  }

  /* ------------------------------------------------------------------
     THE TWO THINGS THIS PLATFORM CAN ACTUALLY GRANT.

     Row scope and column access belong to D365 and stay read-only. Report
     access and platform roles exist only here, so these are the only write
     methods on this client that change anybody's access.

     None of them sends who the actor is. The database derives that from the
     object id in the signed token; a body that named its own actor would let
     anyone who reached the API sign somebody else's name to a grant.
     ------------------------------------------------------------------ */

  adminReports(): Promise<AdminReport[]> {
    return this.#send('GET', '/api/v1/admin/permissions/reports')
  }

  reportAccess(reportSlug: string): Promise<ReportAccessEntry[]> {
    return this.#send('GET', `/api/v1/admin/permissions/access?reportSlug=${encodeURIComponent(reportSlug)}`)
  }

  grantReportAccess(body: GrantAccessRequest): Promise<GrantAccessResult> {
    return this.#send('POST', '/api/v1/admin/permissions/access', body)
  }

  revokeReportAccess(emails: string, reportSlug: string, reason?: string): Promise<{ removed: number }> {
    const q = new URLSearchParams({ emails, reportSlug })
    if (reason) q.set('reason', reason)
    return this.#send('DELETE', `/api/v1/admin/permissions/access?${q}`)
  }

  /** Which permission gates which column. Readable by either admin role. */
  columnGovernance(reportSlug: string): Promise<ColumnGovernance> {
    return this.#send('GET', `/api/v1/admin/permissions/columns?reportSlug=${encodeURIComponent(reportSlug)}`)
  }

  /** Re-gate one column. System Admin only, and the reason is mandatory. */
  setColumnPermissions(body: SetColumnPermissionsRequest): Promise<ColumnPermissionChange> {
    return this.#send('POST', '/api/v1/admin/permissions/columns', body)
  }

  /* --------------------------------------------------------------------
     COLUMN DEFINITION UNDER THE NEW MODEL (060, D53): who sees each column,
     and the column groups, one report each. System Admin only.
     -------------------------------------------------------------------- */

  /** One report's columns and column groups, with the counts. */
  columnDefinition(reportSlug: string): Promise<ColumnDefinition> {
    return this.#send('GET', `/api/v1/admin/column-definition/${encodeURIComponent(reportSlug)}`)
  }

  /** What setting one column would do, people before and after; nothing changed. */
  columnSettingPreview(reportColumnId: number, body: ColumnSettingBody): Promise<ColumnSettingEffect> {
    return this.#send('POST', `/api/v1/admin/column-definition/columns/${reportColumnId}/preview`, body)
  }

  /** Set who sees one column; the reason is required, the version read is quoted. */
  columnSettingSave(reportColumnId: number, body: ColumnSettingBody): Promise<ColumnSettingEffect> {
    return this.#send('PUT', `/api/v1/admin/column-definition/columns/${reportColumnId}`, body)
  }

  /** One column group and everything that uses it. */
  columnGroup(groupId: number): Promise<ColumnGroupPage> {
    return this.#send('GET', `/api/v1/admin/column-groups/${groupId}`)
  }

  /** Make a column group for one report. */
  columnGroupCreate(body: ColumnGroupBody): Promise<ColumnGroupEffect> {
    return this.#send('POST', '/api/v1/admin/column-groups', body)
  }

  /** Rename or describe a column group (its report never changes). */
  columnGroupSave(groupId: number, body: ColumnGroupBody): Promise<ColumnGroupEffect> {
    return this.#send('PUT', `/api/v1/admin/column-groups/${groupId}`, body)
  }

  /** Delete a column group nothing uses (409 COLUMN_GROUP_IN_USE otherwise, saying where). */
  columnGroupDelete(groupId: number, expectedVersion: string, reason: string): Promise<ColumnGroupEffect> {
    return this.#send('DELETE', `/api/v1/admin/column-groups/${groupId}`, { expectedVersion, reason })
  }

  /* --------------------------------------------------------------------
     THE ACCESS MAP'S SCREENS (new model; 05 section 6.7). System Admin or
     System Master Admin only, the new model only (404 NEW_MODEL_ONLY
     before the switch hour). People by Mindzer person ID, never by email.
     -------------------------------------------------------------------- */

  /**
   * Access > People: everyone Mindzer sends, filtered on the server. A POST, though
   * it reads: the search matches emails, and one in a query string would reach the
   * access logs.
   */
  accessPeople(filter: AccessPeopleFilter = {}): Promise<AccessPeople> {
    return this.#send('POST', '/api/v1/admin/people/query', filter)
  }

  /** One person's page: can they enter, what they see and why, warnings. */
  accessPerson(id: number): Promise<AccessPersonPage> {
    return this.#send('GET', `/api/v1/admin/people/${id}`)
  }

  /** The person's staff, direct first: one level at a time. */
  accessStaff(id: number, top?: number): Promise<AccessStaff> {
    return this.#send('GET', `/api/v1/admin/people/${id}/staff${top ? `?top=${top}` : ''}`)
  }

  /** The person's history, newest first; a page older than `before`. */
  accessHistory(id: number, kind?: string, before?: string): Promise<AccessHistory> {
    const q = new URLSearchParams()
    if (kind) q.set('kind', kind)
    if (before) q.set('before', before)
    const qs = q.toString()
    return this.#send('GET', `/api/v1/admin/people/${id}/history${qs ? `?${qs}` : ''}`)
  }

  /** The D365 codes an admin may pick: every company, or one company's units and teams. */
  accessOrgCodes(company?: string): Promise<AccessOrgCodes> {
    return this.#send('GET', `/api/v1/admin/org${company ? `?company=${encodeURIComponent(company)}` : ''}`)
  }

  /** What saving their own settings would do; nothing changes. */
  accessPersonSettingsPreview(id: number, body: AccessPersonSettingsBody): Promise<AccessPersonEffect> {
    return this.#send('POST', `/api/v1/admin/people/${id}/settings/preview`, body)
  }

  /** Save their own settings: the whole state, a reason, the version read. */
  accessPersonSettingsSave(id: number, body: AccessPersonSettingsBody): Promise<AccessPersonEffect> {
    return this.#send('PUT', `/api/v1/admin/people/${id}/settings`, body)
  }

  /** What adding, changing or removing an extra scope would do. */
  accessExtraScopePreview(id: number, body: AccessExtraScopeBody): Promise<AccessPersonEffect> {
    return this.#send('POST', `/api/v1/admin/people/${id}/scopes/preview`, body)
  }

  /** Add an extra scope in another company. */
  accessExtraScopeAdd(id: number, body: AccessExtraScopeBody): Promise<AccessPersonEffect> {
    return this.#send('POST', `/api/v1/admin/people/${id}/scopes`, body)
  }

  /** Change an extra scope. */
  accessExtraScopeSave(id: number, scopeId: number, body: AccessExtraScopeBody): Promise<AccessPersonEffect> {
    return this.#send('PUT', `/api/v1/admin/people/${id}/scopes/${scopeId}`, body)
  }

  /** Remove an extra scope; a reason is required. */
  accessExtraScopeRemove(id: number, scopeId: number, expectedVersion: string | null, reason: string): Promise<AccessPersonEffect> {
    return this.#send('DELETE', `/api/v1/admin/people/${id}/scopes/${scopeId}`, { expectedVersion, reason })
  }

  /** Link the person to a sign-in by hand, or unlink; preview first. The email travels in the body. */
  accessSignInLink(id: number, body: { signInEmail?: string; unlink: boolean; reason: string; preview: boolean }): Promise<AccessSignInLinkEffect> {
    return this.#send('POST', `/api/v1/admin/people/${id}/sign-in-link`, body)
  }

  /**
   * Login As for someone who never signed in (D51): MBI finds their Microsoft account by
   * their Mindzer email, registers it with no access and links it, and answers with the
   * account to act as. A refusal says why (no such account, not set up, ...).
   */
  accessFindAccount(id: number): Promise<{ outcome: string; objectId: string }> {
    return this.#send('POST', `/api/v1/admin/people/${id}/find-account`, {})
  }

  /** Access > Approvals: who is waiting, a page at a time, and the counts. A body, as People's: the search matches emails. */
  accessApprovals(filter: AccessApprovalFilter): Promise<AccessApprovals> {
    return this.#send('POST', '/api/v1/admin/approvals/query', filter)
  }

  /** One person's approval card: everything an approver must see. */
  accessApprovalCard(id: number): Promise<AccessApprovalCard> {
    return this.#send('GET', `/api/v1/admin/approvals/${id}`)
  }

  /** Approve one person, quoting the card's warnings key (409 if they changed since). */
  accessApprove(id: number, warningsKey: string, note?: string): Promise<AccessDecision> {
    return this.#send('POST', `/api/v1/admin/approvals/${id}/approve`, { warningsKey, note })
  }

  /** Access > HR roles: every Mindzer role, set up or not, with its counts. */
  accessHrRoles(): Promise<AccessHrRoles> {
    return this.#send('GET', '/api/v1/admin/hr-roles')
  }

  /** One role for the editor: its settings, every report and every column group. */
  accessHrRole(id: number): Promise<AccessHrRolePage> {
    return this.#send('GET', `/api/v1/admin/hr-roles/${id}`)
  }

  /** Who holds the role: the People list, filtered to it. */
  accessHrRolePeople(id: number): Promise<AccessPeople> {
    return this.#send('GET', `/api/v1/admin/hr-roles/${id}/people`)
  }

  /** Who changed the role, when and why, newest first; a page older than `before` (17g; review R-26). */
  accessHrRoleHistory(id: number, before?: string): Promise<AccessHistory> {
    return this.#send('GET', `/api/v1/admin/hr-roles/${id}/history${before ? `?before=${encodeURIComponent(before)}` : ''}`)
  }

  /** Before its hierarchy goes on: each holder's rows per report, now, Type 1 and Type 2 (D49). */
  accessHrRoleHierarchyPreview(id: number): Promise<AccessHierarchyPreview> {
    return this.#send('GET', `/api/v1/admin/hr-roles/${id}/hierarchy-preview`)
  }

  /** What saving (or, with remove, stopping) would do: the same procedure, nothing changed. */
  accessHrRolePreview(id: number, body: AccessHrRoleBody): Promise<AccessHrRoleEffect> {
    return this.#send('POST', `/api/v1/admin/hr-roles/${id}/preview`, body)
  }

  /** Save the role's whole state; the reason is required, the version the editor read is quoted. */
  accessHrRoleSave(id: number, body: AccessHrRoleBody): Promise<AccessHrRoleEffect> {
    return this.#send('PUT', `/api/v1/admin/hr-roles/${id}`, body)
  }

  /** Stop using the role in MBI: its holders lose what it gave them. */
  accessHrRoleRemove(id: number, expectedVersion: string | null, reason: string): Promise<AccessHrRoleEffect> {
    return this.#send('DELETE', `/api/v1/admin/hr-roles/${id}`, { expectedVersion, reason })
  }

  /** Access > Problems: each category with how many items it holds. */
  accessProblems(): Promise<AccessProblems> {
    return this.#send('GET', '/api/v1/admin/problems')
  }

  /** One category's items, a page of 1,000 at a time (17g; review R-20). */
  accessProblemItems(category: string, page = 1): Promise<AccessProblemItems> {
    return this.#send('GET', `/api/v1/admin/problems/${encodeURIComponent(category)}${page > 1 ? `?page=${page}` : ''}`)
  }

  /** Access > Mindzer sync: is the copy up to date, and the last attempts. */
  accessSync(): Promise<AccessSync> {
    return this.#send('GET', '/api/v1/admin/mindzer-sync')
  }

  /** A held or refused update: who would gain what. */
  accessHeldPull(pullId: number): Promise<AccessHeldPull> {
    return this.#send('GET', `/api/v1/admin/mindzer-sync/held/${pullId}`)
  }

  /** "Accept this update": the next hourly run applies it, once. A reason is required. */
  accessAcceptPull(pullId: number, reason: string): Promise<AccessAcceptResult> {
    return this.#send('POST', `/api/v1/admin/mindzer-sync/accept/${pullId}`, { reason })
  }

  /** The email domains whose people may sign in. */
  accessEmailDomains(): Promise<AccessEmailDomains> {
    return this.#send('GET', '/api/v1/admin/mindzer-sync/email-domains')
  }

  /** What adding or removing a domain would do (how many would stop being able to sign in). */
  accessEmailDomainPreview(domain: string, remove: boolean): Promise<AccessEmailDomainEffect> {
    return this.#send('POST', '/api/v1/admin/mindzer-sync/email-domains/preview', { domain, remove })
  }

  /** Add or remove a domain; takes effect at the next hourly update. */
  accessEmailDomainSave(domain: string, remove: boolean, reason: string): Promise<AccessEmailDomainEffect> {
    return this.#send('PUT', '/api/v1/admin/mindzer-sync/email-domains', { domain, remove, reason })
  }

  /** Take an approval back; a reason is required. They are waiting again (D37). */
  accessWithdraw(id: number, reason: string): Promise<AccessDecision> {
    return this.#send('POST', `/api/v1/admin/approvals/${id}/withdraw`, { reason })
  }

  /* --------------------------------------------------------------------
     DEVELOPER MODE (028). System Admin only, and the measure route answers
     404 rather than 403 when the switch is off -- an unauthorised caller
     learns nothing about whether the feature exists.
     -------------------------------------------------------------------- */

  /** The four switches and who last moved each one. */
  /* ------------------------------------------------------------------
     THIS READER'S OWN SCREEN — the column order, hidden columns, widths
     and pinning they arranged on each grid.

     Under /me/, which means "whoever this session is" — the person being
     acted as, under Login As, exactly as everywhere else. No role and no
     scope is involved: a column order is not report data, and somebody
     granted no reports at all still has a right to their own screen.

     ALL THREE MAY FAIL QUIETLY. Every grid works without them, which is
     how the platform behaved until 24 Sep 2026; the caller catches and
     carries on rather than putting a panel in front of the figures.
     ------------------------------------------------------------------ */

  /** Every layout this reader has, in one call. Asked once per session. */
  gridLayouts(): Promise<StoredGridLayout[]> {
    return this.#send('GET', '/api/v1/me/grid-layouts')
  }

  /** Store one. `layout` is this client's own JSON; the server never reads it. */
  saveGridLayout(gridId: string, layout: string): Promise<void> {
    return this.#send('PUT', `/api/v1/me/grid-layouts/${encodeURIComponent(gridId)}`, { layout })
  }

  /**
   * Forget one, so the grid ships its own defaults for ever after.
   *
   * Deliberately not "store the defaults": a column added next year should
   * reach somebody who once pressed Reset, and storing today's arrangement
   * would freeze the grid as it was on the day they pressed it.
   */
  forgetGridLayout(gridId: string): Promise<void> {
    return this.#send('DELETE', `/api/v1/me/grid-layouts/${encodeURIComponent(gridId)}`)
  }

  /**
   * Remember one setting (036) -- today only 'currency': 'local', 'usd' or a
   * code. Read back through /me, which every report waits for, never here.
   */
  saveReaderSetting(key: 'currency', value: string): Promise<void> {
    return this.#send('PUT', `/api/v1/me/settings/${encodeURIComponent(key)}`, { value })
  }

  /**
   * This reader's named views for one report, every tab of it, in one call.
   *
   * A view saved on a tab the report no longer has is already filtered out by
   * the database, which joins plat.Report_Page_MBI — so nothing here has to know
   * which pages exist.
   */
  readerViews(report: string): Promise<StoredReaderView[]> {
    return this.#send('GET', `/api/v1/me/views?report=${encodeURIComponent(report)}`)
  }

  /**
   * Save one. The NAME is the identity: saving over an existing name on the
   * same tab replaces it, rather than leaving two entries a reader cannot tell
   * apart. Names are unique per person per tab, so two readers may both have a
   * view called "Mine".
   */
  saveReaderView(options: {
    report: string
    page: string
    name: string
    view: string
    makeDefault?: boolean
  }): Promise<{ viewId: number }> {
    return this.#send('POST', '/api/v1/me/views', {
      report: options.report,
      page: options.page,
      name: options.name,
      view: options.view,
      makeDefault: options.makeDefault ?? false,
    })
  }

  deleteReaderView(viewId: number): Promise<void> {
    return this.#send('DELETE', `/api/v1/me/views/${viewId}`)
  }

  /**
   * Make one the tab's default, or clear it.
   *
   * Separate from saving, deliberately: choosing a default must not overwrite
   * that view's payload with whatever happens to be on screen.
   */
  setDefaultReaderView(viewId: number, isDefault = true): Promise<void> {
    return this.#send('POST', `/api/v1/me/views/${viewId}/default`, { isDefault })
  }

  /**
   * Give one a different name.
   *
   * A call of its own rather than a save under the new name: saving replaces BY
   * NAME, so renaming onto a name already in use would silently overwrite that
   * view. The server answers 409 for a clash, which the menu shows in the row.
   */
  renameReaderView(viewId: number, name: string): Promise<void> {
    return this.#send('POST', `/api/v1/me/views/${viewId}/name`, { name })
  }

  devFlags(): Promise<DevFlag[]> {
    return this.#send('GET', '/api/v1/admin/dev-mode')
  }

  /** Flip one. The reason is mandatory and the database enforces it. */
  setDevFlag(body: SetDevFlagRequest): Promise<{ outcome: string }> {
    return this.#send('POST', '/api/v1/admin/dev-mode', body)
  }

  /**
   * What one figure on a report means.
   *
   * Resolves to null for BOTH "nothing documented" and "developer mode is
   * off", because the server answers 404 to each and deliberately does not
   * distinguish them. Callers render the same empty state for both.
   */
  async measureDoc(report: string, surface: string, key: string): Promise<MeasureDoc | null> {
    const q = new URLSearchParams({ report, surface, key })
    try {
      return await this.#send('GET', `/api/v1/dev/measure?${q}`)
    } catch (e) {
      if (e instanceof ApiError && e.failure.kind === 'not-found') return null
      throw e
    }
  }

  /**
   * Which keys on a report have documentation behind them, so a page can make
   * exactly those headers clickable.
   *
   * NOT ENTITLED IS NOT THE SAME AS BROKEN, and this method used to treat them
   * as one thing. The server answers 200 with an EMPTY LIST to anyone who is
   * not a System Admin or has the switch off — "nothing to offer you" is a
   * complete answer and needs no error handling. A genuine fault answers 500.
   * Those are already distinguishable on the wire; swallowing both into `[]`
   * threw that away.
   *
   * IT COST A DEPLOY CYCLE. The VM's managed identity had no EXECUTE on
   * plat.usp_List_Measure_Docs_MBI — the grant script had not been re-run since the
   * procedure was added — so every call failed, this returned `[]`, and not one
   * column header became clickable. No error on screen, nothing in the console,
   * and a report that looked exactly like one where developer mode was simply
   * switched off. The diagnosis took a database session; it should have taken a
   * glance at the screen.
   *
   * So a real failure now propagates and the provider says so. An optional
   * affordance should degrade quietly when it is not FOR you, and complain when
   * it is broken.
   */
  measureRefs(report: string): Promise<MeasureRef[]> {
    const q = new URLSearchParams({ report })
    return this.#send('GET', `/api/v1/dev/measures?${q}`)
  }
  roleHolders(): Promise<RoleHolder[]> {
    return this.#send('GET', '/api/v1/admin/permissions/roles')
  }

  setPlatformRole(body: SetRoleRequest): Promise<{ outcome: string }> {
    return this.#send('POST', '/api/v1/admin/permissions/roles', body)
  }

  /**
   * The News feed. Open to every signed-in caller — News is the one page every
   * identity can reach, so a person with no reports gets an empty list rather
   * than a refusal.
   *
   * @param includePast Ask for expired and removed items. The database honours
   *   it only for a System Admin and ignores it otherwise — silently, because
   *   refusing would tell an ordinary caller that a history exists.
   */
  /**
   * @param viewAs Render the feed as this person sees it. View As means the
   *   caller BECOMES the target, so the borrowed feed is exactly the borrowed
   *   sidebar: bypass is surrendered and past updates follow the target's role.
   *   Refused with 403 unless the caller may borrow a surface, in the API and
   *   again in the database, which audits the attempt either way.
   */
  news(includePast?: boolean): Promise<NewsItem[]> {
    const q = new URLSearchParams()
    if (includePast) q.set('includePast', 'true')
    const s = q.toString()
    return this.#send('GET', `/api/v1/news${s ? `?${s}` : ''}`)
  }

  /** Publish a report update. System Admin only, in the API and again in the database. */
  publishNews(body: PublishNewsRequest): Promise<{ newsId: number }> {
    return this.#send('POST', '/api/v1/admin/news', body)
  }

  /** Correct a published update. Does not move its publish date or its expiry. */
  updateNews(newsId: number, body: UpdateNewsRequest): Promise<{ newsId: number }> {
    return this.#send('PUT', `/api/v1/admin/news/${newsId}`, body)
  }

  /**
   * Remove an update from the feed. POST rather than DELETE because it is a
   * retirement, not a deletion — the row and its history stay.
   */
  retireNews(newsId: number, reason?: string): Promise<{ newsId: number; removed: boolean }> {
    return this.#send('POST', `/api/v1/admin/news/${newsId}/retire`, { reason: reason ?? null })
  }

  maintenanceStatus(reportSlug?: string): Promise<MaintenanceStatus> {
    const q = reportSlug ? `?reportSlug=${encodeURIComponent(reportSlug)}` : ''
    return this.#send('GET', `/api/v1/admin/maintenance${q}`)
  }

  setMaintenance(body: SetMaintenanceRequest): Promise<{ scope: string; expectedEndUtc: string }> {
    return this.#send('POST', '/api/v1/admin/maintenance', body)
  }

  clearMaintenance(scope: string): Promise<{ scope: string; windowsClosed: number }> {
    return this.#send('DELETE', `/api/v1/admin/maintenance?scope=${encodeURIComponent(scope)}`)
  }

  /* ------------------------------------------------------------ material planning
     Ten screens, one report. Every call takes the same optional viewAs the
     other reports take; nothing about identity is ever sent, and the currency
     is a display choice the procedure validates again.
     --------------------------------------------------------------------------- */

  /**
   * Every Material Planning call builds its query string the same way: each
   * option key IS its query-string name, and a key is sent only when it holds
   * something. Ten screens' worth of `if (options.x) q.set('x', ...)` was the
   * kind of repetition where one copy drifts from the others.
   */
  #mp<T>(path: string, options: Record<string, string | number | boolean | Currency | undefined>): Promise<T> {
    return this.#send('GET', this.#mpPath(path, options))
  }

  /** The same, with what the new model says about the read (M8). */
  #mpRead<T>(path: string, options: Record<string, string | number | boolean | Currency | undefined>): Promise<Answer<T>> {
    return this.#read(this.#mpPath(path, options))
  }

  #mpPath(path: string, options: Record<string, string | number | boolean | Currency | undefined>): string {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(options)) {
      if (v === undefined || v === null || v === '') continue
      /* CURRENCY IS THE ONE VALUE HERE THAT IS NOT A SCALAR, so it is the one
         that cannot be String()'d. Handled once rather than at all nine call
         sites — and it fixes an old inconsistency on the way: this builder used
         to send `currency=local` explicitly while the other twelve endpoints
         expressed local by OMITTING the parameter. Same meaning, two spellings,
         and the kind of difference that makes a later reader doubt which is
         load-bearing. */
      if (k === 'currency') {
        const ccy = toParam(v as Currency)
        if (ccy) q.set('currency', ccy)
        continue
      }
      q.set(k, String(v))
    }
    const qs = q.toString()
    return `/api/v1/reports/material-planning/${path}${qs ? `?${qs}` : ''}`
  }

  /** The option lists behind the standing filter bar, scoped to what the caller's rows contain. */
  materialPlanningFilters(options: Record<string, never> = {}): Promise<MpFilterRow[]> {
    return this.#mp('filters', options)
  }

  /** The quantity tiles — the Power BI cards, card for card. One row. */
  materialPlanningOverview(
    options: { company?: string; bu?: string; itemGroup?: string; supplier?: string; currency?: Currency } = {},
  ): Promise<Answer<MpOverview>> {
    return this.#mpRead('overview', options)
  }

  /** The donut (cost by unit) or the bar chart (quantity sold by company against last year), one shape. */
  materialPlanningBreakdown(
    options: {
      kind: MpBreakdownKind; year?: number; company?: string; bu?: string; itemGroup?: string
      supplier?: string; currency?: Currency
    },
  ): Promise<Answer<MpBreakdownRow[]>> {
    return this.#mpRead('overview-breakdown', options)
  }

  /**
   * Stock on hand, one row per item and warehouse. PAGED: the planning view is
   * 23,496 rows. Rows with no inventory record (55% of production) are hidden
   * unless `includeEmpty` is set; `sortBy` defaults to on-hand, descending.
   */
  materialPlanningStock(
    options: {
      company?: string; bu?: string; itemGroup?: string; warehouse?: string; supplier?: string
      search?: string; includeEmpty?: boolean; sortBy?: MpStockSort; groupBy?: MpStockGroupBy
      currency?: Currency; page?: number; pageSize?: number; sortDesc?: boolean
    } = {},
  ): Promise<Answer<MpStockRow[]>> {
    return this.#mpRead('stock', options)
  }

  /**
   * Purchase orders. Three Power BI pages behind one status picker; 'open' is
   * line status 1 or 2 — the order-page definition. The tiles use the other one.
   */
  materialPlanningPurchaseOrders(
    options: {
      status?: MpOrderStatus; approvalStatus?: number; company?: string; supplier?: string
      modeOfDelivery?: string; supplierCategory?: string; bu?: string; itemGroup?: string
      search?: string; currency?: Currency; page?: number; pageSize?: number
      sortBy?: string; sortDesc?: boolean
    } = {},
  ): Promise<Answer<MpPurchaseOrderRow[]>> {
    return this.#mpRead('purchase-orders', options)
  }

  /**
   * The three shipment screens, which differ only in their date rule:
   * 'tracker' is confirmed open lines still to arrive, newest readiness first;
   * 'arriving-soon' is an ETA inside thirty days; 'late' is an ETA already
   * past with no goods receipt.
   */
  materialPlanningShipments(
    options: {
      view?: MpShipmentView; company?: string; supplier?: string; modeOfDelivery?: string
      shipmentStatus?: string; orderProgress?: string; orderStatus?: string
      bu?: string; itemGroup?: string; supplierCategory?: string; search?: string; currency?: Currency
      page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean
    } = {},
  ): Promise<Answer<MpShipmentRow[]>> {
    return this.#mpRead('shipments', options)
  }

  /** Twelve months against the same months a year earlier, quantity signed. Year null means the latest in the caller's data. */
  materialPlanningSalesVsLy(
    options: {
      year?: number; company?: string; bu?: string; itemGroup?: string; supplier?: string
      currency?: Currency
    } = {},
  ): Promise<Answer<MpSalesMonth[]>> {
    return this.#mpRead('sales-vs-ly', options)
  }

  /** Quantity sold per product, ABSOLUTE, against the prior year. Paged. */
  materialPlanningSalesPerformance(
    options: {
      year?: number; company?: string; bu?: string; itemGroup?: string; supplier?: string
      search?: string; page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean
    } = {},
  ): Promise<Answer<MpSalesPerformanceRow[]>> {
    return this.#mpRead('sales-performance', options)
  }

  /** The Item Status grid: one row per item. Empty items hidden unless `includeEmpty`. Paged. */
  materialPlanningItemStatus(
    options: {
      company?: string; bu?: string; itemGroup?: string; supplier?: string; search?: string
      year?: number; includeEmpty?: boolean; currency?: Currency
      page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean
    } = {},
  ): Promise<Answer<MpItemStatusRow[]>> {
    return this.#mpRead('item-status', options)
  }

  /** The Item Status lookup: one row for the selection, a year against the year before. */
  materialPlanningItemSummary(
    options: {
      company?: string; bu?: string; itemGroup?: string; supplier?: string; item?: string
      year?: number; currency?: Currency
    } = {},
  ): Promise<Answer<MpItemSummary>> {
    return this.#mpRead('item-summary', options)
  }

  /**
   * The On Order tooltip: which open orders make up that number, for one item.
   * `item` is the only required parameter anywhere on this report — without it
   * the server would have to read every open order in reach to draw a hover
   * card, so it refuses instead.
   */
  materialPlanningItemOrders(
    options: { item: string; company?: string; currency?: Currency },
  ): Promise<MpItemOrderRow[]> {
    return this.#mp('item-orders', options)
  }

  /* ------------------------------------------------------------ inventory aging
     Three screens, four procedures (040). The same query-string rule as
     Material Planning's builder: each option key IS its parameter name, and a
     key is sent only when it holds something.
     --------------------------------------------------------------------------- */

  #inv<T>(path: string, options: Record<string, string | number | boolean | Currency | undefined>): Promise<T> {
    return this.#send('GET', this.#invPath(path, options))
  }

  /** The same, with what the new model says about the read (M8). */
  #invRead<T>(path: string, options: Record<string, string | number | boolean | Currency | undefined>): Promise<Answer<T>> {
    return this.#read(this.#invPath(path, options))
  }

  #invPath(path: string, options: Record<string, string | number | boolean | Currency | undefined>): string {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(options)) {
      if (v === undefined || v === null || v === '') continue
      if (k === 'currency') {
        const ccy = toParam(v as Currency)
        if (ccy) q.set('currency', ccy)
        continue
      }
      q.set(k, String(v))
    }
    const qs = q.toString()
    return `/api/v1/reports/inventory-aging/${path}${qs ? `?${qs}` : ''}`
  }

  /** The standing filter bar's option lists, scoped to what the caller's rows contain. */
  inventoryAgingFilters(options: Record<string, never> = {}): Promise<InvFilterRow[]> {
    return this.#inv('filters', options)
  }

  /** Every tile on the three screens, in one row. `ageBand` narrows to one age bucket. */
  inventoryAgingOverview(
    options: {
      company?: string; bu?: string; bt?: string; warehouse?: string; search?: string; currency?: Currency
      ageBand?: InvAgeBand
    } = {},
  ): Promise<Answer<InvOverview>> {
    return this.#invRead('overview', options)
  }

  /**
   * The Aging grid at one of six groupings, with the age buckets. Paged and
   * sorted on the server. The `path*` options narrow it to one drill-down
   * branch; the list view never sends them.
   */
  inventoryAgingMatrix(
    options: {
      groupBy?: InvGroupBy; company?: string; bu?: string; bt?: string; warehouse?: string; search?: string
      currency?: Currency; sortBy?: string; sortDesc?: boolean; page?: number; pageSize?: number
      pathCompany?: string; pathUnit?: string; pathUnitNone?: boolean
      pathTeam?: string; pathTeamNone?: boolean; pathItem?: string
      ageBand?: InvAgeBand
    } = {},
  ): Promise<Answer<InvMatrixRow[]>> {
    return this.#invRead('matrix', options)
  }

  /** One row per lot, behind an expiry window. Paged and sorted on the server. */
  inventoryAgingLots(
    options: {
      window?: InvWindow; company?: string; bu?: string; bt?: string; warehouse?: string; search?: string
      currency?: Currency; sortBy?: string; sortDesc?: boolean; page?: number; pageSize?: number
      ageBand?: InvAgeBand
    } = {},
  ): Promise<Answer<InvLotRow[]>> {
    return this.#invRead('lots', options)
  }

  /* ------------------------------------------------- statement of account
     No currency parameter: a statement is in each transaction's own currency.
     ------------------------------------------------------------------------ */

  /**
   * Up to 50 customer records whose account number or name contains `search`,
   * each with every account of it the reader may see -- one row per account.
   */
  /** The Company box: every company the reader can see a customer in, with how many. */
  statementCompanies(): Promise<StatementCompany[]> {
    return this.#send('GET', '/api/v1/reports/statement-of-account/companies')
  }

  /**
   * The customer search. With companies ticked, only customers found by an
   * account in one of them -- each still listed with all its visible accounts,
   * which are what the Company box then offers. None ticked is every company.
   */
  statementCustomers(search: string, companies: string[] = []): Promise<StatementCustomer[]> {
    const q = new URLSearchParams()
    if (search.trim()) q.set('search', search.trim())
    if (companies.length > 0) q.set('companies', companies.join(','))
    const qs = q.toString()
    return this.#send('GET', `/api/v1/reports/statement-of-account/customers${qs ? `?${qs}` : ''}`)
  }

  /**
   * One customer record's statement, Full or Open, in the companies ticked: a
   * header per company and every line. Dates are YYYY-MM-DD. The companies are
   * checked again on the server; naming one grants nothing.
   *
   * BOTH DATES OR NEITHER. The procedures read a missing date as "the default
   * period" and reset BOTH, so sending one alone would quietly discard it.
   * Neither is what the screen sends until the reader changes the period: the
   * server then picks 1 January to ITS today.
   */
  statement(party: string, companies: string[], kind: StatementKind, from?: string, to?: string): Promise<Answer<StatementResponse>> {
    const q = new URLSearchParams({ party, companies: companies.join(','), kind })
    if (from && to) {
      q.set('from', from)
      q.set('to', to)
    }
    return this.#read(`/api/v1/reports/statement-of-account/statement?${q.toString()}`)
  }

  async #send<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await this.#exchange(method, path, body)
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
  }

  /**
   * A report read with what the new model says about it: the body as #send
   * returns it, and the response's account of which figures the reader sees
   * (answer.ts). Null under today's model, which says nothing.
   */
  async #read<T>(path: string): Promise<Answer<T>> {
    const response = await this.#exchange('GET', path)
    const data = response.status === 204 ? (undefined as T) : ((await response.json()) as T)
    return { data, vis: visibilityOf(response.headers) }
  }

  /** The request itself: the one place a call is made, answered, or refused. */
  async #exchange(method: string, path: string, body?: unknown): Promise<Response> {
    const token = await this.#getToken()
    if (!token) throw new ApiError({ kind: 'unauthenticated' })

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    /* LOGIN AS, IN ONE PLACE.
       -----------------------------------------------------------------------
       Every request this client makes passes through here, so the acting
       session is attached once and cannot be forgotten. That is the whole
       difference from `?viewAs=`, which had to be remembered at 30 separate
       methods and was — correctly — remembered at all 30, while the 20
       administration methods deliberately did not carry it and therefore ran as
       the administrator under a banner naming somebody else.

       There is no equivalent decision to make here. The header goes on
       everything, so an acting session is acting for every call it makes. */
    const target = actingAs()
    if (target) headers[ACT_AS_HEADER] = target

    let response: Response
    try {
      response = await fetch(path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        // Report data is per-user. A cached response is a response that could
        // be served to the wrong person by a proxy that does not know that.
        cache: 'no-store',
        credentials: 'omit',
      })
    } catch {
      throw new ApiError({ kind: 'error', title: 'Could not reach the platform.' })
    }

    if (response.ok) return response

    const failure = await decodeFailure(response)
    // Tell the app before telling the caller. A window opened while somebody was
    // already working reaches them only as a refusal on whatever they touched
    // next; App listens for this and replaces the screen, so being inside when
    // the window opens looks the same as arriving after it.
    maintenanceDetected(failure)
    // The same for a refusal at the door (05 6.2): an approval taken back while
    // they work turns the whole screen into "Access denied", not panel by panel.
    entryRefusedDetected(failure)
    throw new ApiError(failure)
  }
}

/**
 * The four standing sales filters plus period, currency and view-as, encoded
 * once. 'ytd' is the server default and is never sent; 'custom' is sent only
 * with both bounds — half a range would just fall back to 'ytd' server-side.
 */
function salesQuery(
  options: SalesFilters &
    SalesRange & { period?: SalesPeriod; currency?: Currency },
): URLSearchParams {
  const q = new URLSearchParams()
  if (options.period === 'mtd') q.set('period', 'mtd')
  if (options.period === 'custom' && options.from && options.to) {
    q.set('period', 'custom')
    q.set('from', options.from)
    q.set('to', options.to)
  }
  // Company is multi-select; an empty list is simply not sent, which the
  // procedures read as every company the caller may see.
  if (options.company?.length) q.set('company', options.company.join(','))
  if (options.bu) q.set('bu', options.bu)
  if (options.bt) q.set('bt', options.bt)
  if (options.sp) q.set('sp', options.sp)
  // Every sales call goes through here, so the standing bar narrows the KPI
  // strip, the grid, the boards, the trend, the tree and the ledger from one
  // place — which is the only reason those six can be trusted to agree.
  // U+001F between members — see SalesFilters.sup for why not a comma.
  if (options.sup?.length) q.set('sup', options.sup.join('\u001F'))
  if (options.reg) q.set('reg', options.reg)
  if (options.cust) q.set('cust', options.cust)
  { const ccy = toParam(options.currency); if (ccy) q.set('currency', ccy) }
  return q
}

/**
 * Turns an HTTP failure into one of the kinds the UI knows how to render.
 *
 * The correlation id is extracted wherever it appears, because it is the only
 * thing that lets a user's screenshot be joined to the server log and the audit
 * ledger. A support call without one is a search through millions of rows.
 */
async function decodeFailure(response: Response): Promise<ApiFailure> {
  let payload: Record<string, unknown> = {}
  try {
    payload = (await response.json()) as Record<string, unknown>
  } catch {
    /* A non-JSON error body is still a failure; fall through to the defaults. */
  }

  const correlationId = String(
    payload.correlationId ?? (payload.extensions as Record<string, unknown> | undefined)?.correlationId ?? '',
  )
  const title = typeof payload.title === 'string' ? payload.title : ''

  if (response.status === 401) return { kind: 'unauthenticated' }

  // 404 is a real answer on one route and one route only: /dev/measure returns
  // it for "nothing documented", "developer mode is off" and "you are not a
  // System Admin" alike, so that an unauthorised caller cannot tell the three
  // apart. It is decoded as its own kind rather than falling through to the
  // generic error, because the panel has to distinguish "there is nothing to
  // show you" from "something went wrong" — those are different screens.
  if (response.status === 404) return { kind: 'not-found', correlationId: correlationId || undefined }

  if (response.status === 503) {
    // Not every 503 is a maintenance window. Admission control sheds a heavy
    // report when too many are running at once, and that refusal is JSON with a
    // title but no `scope` — it means "retry in a few seconds", nothing more.
    // Decoded as maintenance it used to fill in scope 'platform', and App then
    // swapped the whole shell for the Platform Closed screen, throwing away the
    // user's drill state, for everyone except a System Admin (who bypasses it,
    // which is why admin testing never saw it). Only MaintenanceMiddleware sends
    // `scope`, and nginx's edge page is not JSON at all (payload stays empty, so
    // no title), so both real windows still fall through to the branch below.
    if (typeof payload.scope !== 'string' && title) {
      return {
        kind: 'error',
        title: typeof payload.detail === 'string' ? payload.detail : title,
        correlationId: correlationId || undefined,
      }
    }

    const retryAfter = Number(response.headers.get('Retry-After') ?? '60')
    return {
      kind: 'maintenance',
      message: typeof payload.detail === 'string' ? payload.detail : 'The platform is temporarily unavailable.',
      scope: typeof payload.scope === 'string' ? payload.scope : 'platform',
      expectedEndUtc: typeof payload.expectedEndUtc === 'string' ? payload.expectedEndUtc : '',
      retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : 60,
    }
  }

  if (response.status === 403) {
    // The API distinguishes these deliberately: one means "you are entitled to
    // no data", the other "you are not an administrator". Different problems,
    // different people to ask, different screens.
    //
    // `explanation` is the API's own sentence for THIS refusal, and carrying it
    // is not a nicety. The screen used to pair whatever title arrived with one
    // hardcoded line — "Your account does not hold the platform role this page
    // requires" — so "Not on the access list for this report" was answered with
    // a sentence about platform roles. Two different refusals, two different
    // fixes, and the panel named the wrong one.
    //
    // ProblemDetails.Detail is stripped globally so nothing internal can leak;
    // this field is written deliberately, per refusal, for a reader.
    const explanation = typeof payload.explanation === 'string' ? payload.explanation : undefined
    // The machine-readable DENIED_* outcome ReportGuards.RefusalFor sends beside
    // the prose. Kept so a screen can branch on the code rather than the title.
    const reason = typeof payload.reason === 'string' ? payload.reason : undefined
    // Under the new model, the precise code, sent only to an administrator signed in as the person.
    const adminReason = typeof payload.adminReason === 'string' ? payload.adminReason : undefined

    return title === 'No data permissions'
      ? { kind: 'no-permissions', correlationId }
      : {
          kind: 'forbidden',
          title: title || 'You do not have access to this.',
          explanation,
          correlationId,
          reason,
          adminReason,
        }
  }

  return {
    kind: 'error',
    title: title || `The platform returned ${response.status}.`,
    correlationId: correlationId || undefined,
    // The DENIED_* code, where the endpoint sent one. See ApiFailure.
    outcome: typeof payload.outcome === 'string' ? payload.outcome : undefined,
    // The access map's code on a 409 (Part 17): branch on it, not on the title.
    reason: typeof payload.reason === 'string' ? payload.reason : undefined,
  }
}
